/**
 * demoPartner.ts — Wrapper around demoState.ts
 *
 * All existing imports continue to work unchanged.
 * Partner variant resolution has moved to demoState.ts — do not add detection logic here.
 *
 * Place at: src/demo/demoPartner.ts
 */

import {
  isPharmacyDemo as _isPharmacyDemo,
  exitDemoState,
} from './demoState';

// ── Types (re-exported for consumers) ────────────────────────────────────────

export type DemoPartner = 'pharmacy' | null;

export interface PharmacyPartnerConfig {
  slug: 'pharmacy';
  displayName: string;
  shortName: string;
  bannerLine: string;
  valueScreenTitle: string;
  ctaLabel: string;
  contactHref: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

export const PHARMACY_CONFIG: PharmacyPartnerConfig = {
  slug: 'pharmacy',
  displayName: 'MyLondonPharmacy',
  shortName: 'MLP',
  bannerLine: 'Pharmacy partner demo — CoreTrak for GLP-1 patients',
  valueScreenTitle: 'Why pharmacies recommend CoreTrak',
  ctaLabel: 'Request a partner demo',
  contactHref: 'mailto:hello@coretrak.app?subject=Pharmacy%20Partner%20Demo',
};

// ── Optional ?name= override ──────────────────────────────────────────────────
// Read once at module load — URL is available at this point post-redirect.

function readPartnerDisplayName(): string | null {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const nameParam = urlParams.get('name');
    if (nameParam) return decodeURIComponent(nameParam);
    return sessionStorage.getItem('coretrak.demo.partnerName');
  } catch {
    return null;
  }
}

let _partnerDisplayName: string | null = null;

// ── Public API ────────────────────────────────────────────────────────────────

/** No-op — kept for AppContext compatibility. demoState.ts handles boot. */
export function initDemoPartner(): DemoPartner {
  return getDemoPartner();
}

export function getDemoPartner(): DemoPartner {
  return _isPharmacyDemo() ? 'pharmacy' : null;
}

export function isPharmacyDemo(): boolean {
  return _isPharmacyDemo();
}

export function getPharmacyConfig(): PharmacyPartnerConfig | null {
  if (!isPharmacyDemo()) return null;
  if (!_partnerDisplayName) {
    _partnerDisplayName = readPartnerDisplayName();
    if (_partnerDisplayName) {
      try { sessionStorage.setItem('coretrak.demo.partnerName', _partnerDisplayName); } catch { /* ignore */ }
    }
  }
  return _partnerDisplayName
    ? { ...PHARMACY_CONFIG, displayName: _partnerDisplayName }
    : PHARMACY_CONFIG;
}

export function getPartnerDisplayName(): string {
  if (!_partnerDisplayName) {
    _partnerDisplayName = readPartnerDisplayName();
  }
  return _partnerDisplayName ?? PHARMACY_CONFIG.displayName;
}

export function clearDemoPartner(): void {
  _partnerDisplayName = null;
  exitDemoState();
  try {
    sessionStorage.removeItem('coretrak.demo.partnerName');
  } catch { /* ignore */ }
}
